import { type GivingDetail, GivingTypeEnum } from 'models';
import { useCallback, useMemo } from 'react';
import { CheckCircleFillIcon, CloseCircleFillIcon, Col, Modal, Row, Tag, Text, Title } from 'ui';
import type { GiveTransactionResult } from 'features/transactions';
import { SharingButtons } from '../components/sharing-buttons';

type P = {
  giving?: GivingDetail;
  isOpened: boolean;
  onClose: () => void;
  result?: GiveTransactionResult;
};

const GivingTransactionResultModal = ({ giving, isOpened, onClose, result }: P) => {
  const isSuccess = useMemo(() => result === 'success', [result]);
  const hasAdditionalInfo = useMemo(
    () => isSuccess && (giving?.type === GivingTypeEnum.CROWDFUNDING || giving?.type === GivingTypeEnum.DONATION),
    [giving?.type, isSuccess],
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const modalProperties = useMemo(() => {
    if (isSuccess) {
      return {
        additionalInfoEnd: <Text>Thank you once again for your generosity and support!</Text>,
        additionalInfoStart: (
          <Text>
            Additionally, we would be immensely grateful if you could share your giving with your friends and family.
            Together, we can make a difference and spread the word about our cause.
          </Text>
        ),
        description: (
          <>
            <Text>Congratulations! Your gift has been successfully processed.</Text>
            {hasAdditionalInfo ? ' ' : <br />}
            <Text>We sincerely appreciate your support.</Text>
          </>
        ),
        modalTitle: 'Giving successful',
        tag: <Tag color="success" icon={<CheckCircleFillIcon size="large" />} rounded size="large" />,
        title: 'Thank you for your generosity',
      };
    }
    return {
      description: <Text>We couldn’t process your gift. Please try again later.</Text>,
      modalTitle: 'Giving failed',
      tag: (
        <Tag color="error" icon={<CloseCircleFillIcon className="status-icon" size="large" />} rounded size="large" />
      ),
      title: 'Something went wrong',
    };
  }, [hasAdditionalInfo, isSuccess]);

  return (
    <Modal
      isOpened={isOpened}
      maskClosable={false}
      onCancel={handleCancel}
      title={modalProperties.modalTitle}
      width="medium"
    >
      <Modal.HugContent>
        <Row className="transaction-result-modal-content" gutter={[0, 'lg']} justify="center">
          <Col>{modalProperties.tag}</Col>
          <Col span={24} textAlign="center">
            <Title>{modalProperties.title}</Title>
          </Col>
          <Col span={24} textAlign="center">
            {modalProperties.description}
          </Col>
          {giving && hasAdditionalInfo ? (
            <>
              <Col span={24} textAlign="center">
                {modalProperties.additionalInfoStart}
              </Col>
              <Col>
                <SharingButtons giving={giving} paymentResult />
              </Col>
              <Col span={24} textAlign="center">
                {modalProperties.additionalInfoEnd}
              </Col>
            </>
          ) : null}
        </Row>
      </Modal.HugContent>
    </Modal>
  );
};

export default GivingTransactionResultModal;
